import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { dashboardRoutes } from '../../constants/routes';
import { shellAssets } from '../../constants/assets';
import { Stepper } from './components/Stepper';
import {
  Step1TripDetails,
  type TripDetailsData,
} from './steps/Step1TripDetails';
import {
  Step2CustomerDetails,
  type CustomerDetailsData,
} from './steps/Step2CustomerDetails';
import {
  Step3VehiclePricing,
  type VehiclePricingData,
} from './steps/Step3VehiclePricing';
import { Step4ReviewConfirm } from './steps/Step4ReviewConfirm';
import { useBookings } from '../../hooks/useBookings';

const currentUser = {
  name: 'Admin',
  email: 'Admin@123gmail.com',
  avatar: shellAssets.avatar,
};

export const NewBookingPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const {
    records,
    addBooking,
    updateBooking,
  } = useBookings();

  const [currentStep, setCurrentStep] = useState(1);
  useEffect(() => {
    const scrollContainer = document.querySelector(
      '.overflow-y-auto.overscroll-contain'
    )

    if (scrollContainer) {
      scrollContainer.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }, [currentStep])
  const [tripData, setTripData] =
    useState<TripDetailsData>({
      serviceType: 'Transfers',
      dayTripId: '',
      journeyType: 'one-way',
      pickupLocation: '',
      dropoffLocation: '',
      pickupDate: '',
      pickupTime: '',
      returnDate: '',
      returnTime: '',
      passengers: '1',
      // children: '0',
      luggage: '0',
      // sourceNumber: '',
      sourceName: '',
      distanceKm: 0,
      durationHours: 1,
      paymentStatus: 'cash',
    });

  const [customerData, setCustomerData] =
    useState<CustomerDetailsData>({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '+43',
      arrivalType: 'flight',
      airline: 'Search Airline',
      flightNumber: '',
      arrivalTerminal: 'Terminal 1',
      arrivalTime: '',
      locationValue: '',
      options: [],
      specialRequirements: '',
      bookingForSomeoneElse: false,
      otherPersonName: '',
      otherPersonPhone: '',
    });

  const [vehicleData, setVehicleData] =
    useState<VehiclePricingData>({
      selectedVehicles: {},
      vehiclePriceOverrides: {},
    });

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  // Pre-fill data if in edit mode
  useEffect(() => {
    if (!isEditMode || records.length === 0) {
      return;
    }

    const existingBooking = records.find(
      (booking) => booking.id === id
    );

    if (!existingBooking) {
      return;
    }

    console.log(
      'EDIT BOOKING DATA:',
      existingBooking
    );

    const serviceTypeMap: Record<
      string,
      TripDetailsData['serviceType']
    > = {
      'Day Trips': 'Day trips',
      'Day trip': 'Day trips',
      daytrips: 'Day trips',
      'Hourly Drivers': 'Hourly drivers',
      'Hourly drivers': 'Hourly drivers',
      Transfers: 'Transfers',
    };

    const pickupDateTime =
      existingBooking.scheduledAt || '';

    const returnDateTime =
      existingBooking.returnDateTime || '';

    /*
     * STEP 1
     */
    setTripData((prev) => ({
      ...prev,
      serviceType:
        serviceTypeMap[existingBooking.service] ||
        prev.serviceType,
      dayTripId: String(existingBooking.dayTripId ?? ''),
      
      sourceName:
        existingBooking.sourceName ??
        existingBooking.source_name ??
        '',

      journeyType:
        existingBooking.returnTrip
          ? 'round-trip'
          : 'one-way',

      pickupLocation:
        existingBooking.route.from || '',

      dropoffLocation:
        existingBooking.route.to || '',

      pickupDate:
        pickupDateTime.split('T')[0] || '',

      pickupTime:
        pickupDateTime
          .split('T')[1]
          ?.substring(0, 5) || '',

      returnDate: returnDateTime
        ? returnDateTime.split('T')[0]
        : '',

      returnTime: returnDateTime
        ? returnDateTime
            .split('T')[1]
            ?.substring(0, 5) || ''
        : '',

      passengers: String(
        existingBooking.passengers ?? 1
      ),

      luggage: String(
        existingBooking.luggage ?? 0
      ),
      paymentStatus:
        existingBooking.paymentMethod === 'pre_paid' ||
        existingBooking.paymentMethod === 'cash' ||
        existingBooking.paymentMethod === 'account_transfer' ||
        existingBooking.paymentMethod === 'paid'
          ? existingBooking.paymentMethod
          : prev.paymentStatus,
    }));

    /*
     * STEP 3
     */
    if (
      existingBooking.vehicle &&
      existingBooking.vehicle !== 'Standard' &&
      !Number.isNaN(
        Number(existingBooking.vehicle)
      )
    ) {
      setVehicleData({
        selectedVehicles: {
          [existingBooking.vehicle]: 1,
        },
        vehiclePriceOverrides:
          existingBooking.vehiclePrice !== undefined
            ? {
                [existingBooking.vehicle]: existingBooking.vehiclePrice,
              }
            : {},
      });
    }

    /*
     * STEP 2 — LOCATION
     *
     * Supported backend formats:
     *
     * 1. Plain text:
     *    "kollam"
     *
     * 2. Google Maps URL:
     *    "https://www.google.com/maps/..."
     *
     * 3. Flight JSON:
     *    {
     *      type: "flight",
     *      company: "...",
     *      flightOrTrainNumber: "...",
     *      arrivalLocation: "...",
     *      arrivalTime: "..."
     *    }
     *
     * 4. Train JSON:
     *    {
     *      type: "train",
     *      ...
     *    }
     *
     * 5. Google Maps JSON:
     *    {
     *      type: "google_maps",
     *      selectedLocation: "...",
     *      position: "..."
     *    }
     */

    const location =
      existingBooking.location;

    let locationCustomerData:
      Partial<CustomerDetailsData> = {};

    let parsedLocation: unknown = location;

    /*
     * The API may return JSON objects as strings.
     * Try to parse them first.
     */
    if (typeof location === 'string') {
      const trimmedLocation =
        location.trim();

      if (trimmedLocation) {
        try {
          parsedLocation =
            JSON.parse(trimmedLocation);
        } catch {
          /*
           * JSON parsing failed.
           * This is therefore ordinary text
           * or a URL.
           */
          parsedLocation = trimmedLocation;
        }
      } else {
        parsedLocation = '';
      }
    }

    /*
     * JSON OBJECT LOCATION
     */
    if (
      parsedLocation &&
      typeof parsedLocation === 'object' &&
      'type' in parsedLocation
    ) {
      const locationData =
        parsedLocation as {
          type?: string;
          company?: string;
          flightOrTrainNumber?: string;
          arrivalLocation?: string;
          arrivalTime?: string;
          selectedLocation?: string;
          position?: string;
        };

      /*
       * FLIGHT
       */
      if (
        locationData.type === 'flight'
      ) {
        locationCustomerData = {
          arrivalType: 'flight',

          airline:
            locationData.company ||
            'Search Airline',

          flightNumber:
            locationData.flightOrTrainNumber ||
            '',

          arrivalTerminal:
            locationData.arrivalLocation ||
            'Terminal 1',

          arrivalTime:
            locationData.arrivalTime || '',

          locationValue: '',
        };
      }

      /*
       * TRAIN
       */
      else if (
        locationData.type === 'train'
      ) {
        locationCustomerData = {
          arrivalType: 'train',

          airline:
            locationData.company || '',

          flightNumber:
            locationData.flightOrTrainNumber ||
            '',

          arrivalTerminal:
            locationData.arrivalLocation ||
            '',

          arrivalTime:
            locationData.arrivalTime || '',

          locationValue: '',
        };
      }

      /*
       * GOOGLE MAPS OBJECT
       *
       * There is intentionally NO separate
       * Google Maps tab.
       *
       * It uses the Location tab.
       */
      else if (
        locationData.type === 'google_maps'
      ) {
        locationCustomerData = {
          arrivalType: 'text',

          locationValue:
            locationData.selectedLocation || '',
        };
      }
    }

    /*
     * PLAIN TEXT OR GOOGLE MAPS URL
     *
     * This is the important part for values
     * such as:
     *
     * "kollam"
     *
     * or:
     *
     * "https://www.google.com/maps/..."
     *
     * Both use the Location tab.
     */
    else if (
      typeof parsedLocation === 'string'
    ) {
      locationCustomerData = {
        arrivalType: 'text',
        locationValue: parsedLocation,
      };
    }

    /*
     * STEP 2 — CUSTOMER DATA
     */
    setCustomerData((prev) => ({
      ...prev,

      firstName:
        existingBooking.customer.name
          .split(' ')[0] || '',

      lastName:
        existingBooking.customer.name
          .split(' ')
          .slice(1)
          .join(' ') || '',

      email:
        existingBooking.customer.email,

      phoneNumber:
        existingBooking.customerPhone ||
        '+43',

      options:
        existingBooking.selectedOptions ||
        [],

      specialRequirements:
        existingBooking.notes || '',

      ...locationCustomerData,
    }));

    console.log(
      'SETTING EDIT CUSTOMER DATA:',
      {
        firstName:
          existingBooking.customer.name
            .split(' ')[0] || '',

        lastName:
          existingBooking.customer.name
            .split(' ')
            .slice(1)
            .join(' ') || '',

        email:
          existingBooking.customer.email,

        phoneNumber:
          existingBooking.customerPhone ||
          '+43',

        options:
          existingBooking.selectedOptions ||
          [],

        specialRequirements:
          existingBooking.notes || '',

        ...locationCustomerData,
      }
    );
  }, [isEditMode, id, records]);

  const validateStep = (step: number) => {
    switch (step) {
      case 1:
        if (
          !tripData.pickupLocation ||
          (tripData.serviceType !== 'Hourly drivers' && !tripData.dropoffLocation) ||
          !tripData.pickupDate ||
          !tripData.pickupTime
        ) {
          return false;
        }

        if (
          tripData.journeyType ===
            'round-trip' &&
          (!tripData.returnDate ||
            !tripData.returnTime)
        ) {
          return false;
        }

        if (
          tripData.serviceType === 'Day trips' &&
          !tripData.dayTripId
        ) {
          return false;
        }

        return true;

      case 2:
        if (
          !customerData.firstName ||
          !customerData.lastName ||
          // !customerData.email ||
          !customerData.phoneNumber ||
          customerData.phoneNumber === '+43'
        ) {
          return false;
        }

        return true;

      case 3:
        return (
          Object.keys(
            vehicleData.selectedVehicles
          ).length > 0
        );

      default:
        return true;
    }
  };

  const isCurrentStepValid =
    validateStep(currentStep);

  const handleNext = async () => {
    if (
      currentStep < 4 &&
      isCurrentStepValid
    ) {
      setCurrentStep(
        (prev) => prev + 1
      );
    } else if (
      currentStep === 4
    ) {
      setIsSubmitting(true);

      const extrasPrices: Record<string, number> = {
        'child-seat': 15,
        'booster-seat': 10,
        'flight-tracking': 0,
        'meet-and-greet': 0,
        'extra-waiting-time': 20,
        'bottled-water': 5,
      };

      const extrasTotal = (customerData.options || []).reduce(
        (acc, opt) => acc + (extrasPrices[opt] || 0),
        0
      );

      const vehiclePrice = vehicleData.vehiclePrice || 0;
      const finalPrice = vehiclePrice + extrasTotal;

      const payload = {
        trip: tripData,
        customer: customerData,
        vehicles: vehicleData.selectedVehicles,
        vehiclePriceOverrides: vehicleData.vehiclePriceOverrides,
        vehicle_price: vehiclePrice,
        final_price: finalPrice,
        estimated_price: finalPrice,
      };

      const success =
        isEditMode && id
          ? await updateBooking(
              id,
              payload
            )
          : await addBooking(payload);

      setIsSubmitting(false);

      if (success) {
        navigate(
          dashboardRoutes.bookings
        );
      } else {
        alert(
          isEditMode
            ? 'Failed to update booking. Please try again.'
            : 'Failed to create booking. Please try again.'
        );
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(
        (prev) => prev - 1
      );
    } else {
      navigate(
        dashboardRoutes.bookings
      );
    }
  };

  return (
    <DashboardLayout
      activeNavId="my-bookings"
      contentGap={15}
      subtitle={
        <Breadcrumb
          items={[
            {
              label: 'Dashboard',
              path: dashboardRoutes.overview,
            },
            {
              label: 'Bookings',
              path: dashboardRoutes.bookings,
            },
            {
              label: isEditMode
                ? 'Edit Booking'
                : 'New Bookings',
            },
          ]}
        />
      }
      title={
        isEditMode
          ? 'Edit Booking'
          : 'Bookings'
      }
      user={currentUser}
    >
      <div className="flex flex-col relative min-h-[calc(100vh-200px)] pb-[100px]">
        {/* Progress Stepper - Sticky */}
        <div className="sticky top-0 z-40 bg-dash-bg pt-4 pb-2 mb-8">
          <Stepper
            currentStep={currentStep}
          />
        </div>

        {/* Step Content */}
        {currentStep === 1 && (
          <Step1TripDetails
            data={tripData}
            updateData={(newData) =>
              setTripData((prev) => ({
                ...prev,
                ...newData,
              }))
            }
          />
        )}

        {currentStep === 2 && (
          <Step2CustomerDetails
            data={customerData}
            updateData={(newData) =>
              setCustomerData((prev) => ({
                ...prev,
                ...newData,
              }))
            }
          />
        )}

        {currentStep === 3 && (
          <Step3VehiclePricing
            data={vehicleData}
            tripData={tripData}
            updateData={(newData: Partial<VehiclePricingData>) =>
              setVehicleData((prev) => ({
                ...prev,
                ...newData,
              }))
            }
          />
        )}

        {currentStep === 4 && (
          <Step4ReviewConfirm
            tripData={tripData}
            customerData={customerData}
            vehicleData={vehicleData}
          />
        )}

        {/* Fixed Footer Actions */}
        <div className="fixed bottom-0 left-0 right-0 h-[80px] bg-[#FAFAFA] border-t border-[#E0E0E0] flex items-center justify-center z-50 lg:ml-[213px]">
          <div className="w-full max-w-[600px] px-[32px] flex items-center justify-center gap-6 mx-auto">
            <button
              onClick={handleBack}
              className="w-[180px] flex items-center justify-center h-[48px] rounded-[24px] bg-[#1A1A1A] text-white font-host text-[15px] font-bold transition-colors hover:bg-black"
            >
              Back
            </button>

            <button
              onClick={handleNext}
              disabled={
                !isCurrentStepValid ||
                isSubmitting
              }
              className={`w-[180px] flex items-center justify-center h-[48px] rounded-[24px] font-host text-[15px] font-bold transition-colors ${
                isCurrentStepValid &&
                !isSubmitting
                  ? 'bg-[#FF6A1A] text-white hover:bg-[#E55A12]'
                  : 'bg-[#E0E0E0] text-[#9E9E9E] cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : currentStep === 4 ? (
                isEditMode
                  ? 'Update Booking'
                  : 'Confirm'
              ) : (
                'Next'
              )}
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};
